import React, { useState } from 'react';
import UsersTab from './UsersTab';
import SettingsTab from './SettingsTab';
import ActivitiesTab from './ActivitiesTab';
import DonationsTab from './DonationsTab';
import HakdashotTab from './HakdashotTab';
import QuizzesTab from './QuizzesTab';
import DashboardTab from './DashboardTab';
import StoriesTab from './StoriesTab';
import RelatedLessonsTab from './RelatedLessonsTab';
import SpecialDaysTab from './SpecialDaysTab';
import NotificationsTab from './NotificationsTab';
import SystemMessagesTab from './SystemMessagesTab';
import ParashotTab from './ParashotTab';
import WeeklyReportTab from './WeeklyReportTab';
import LessonDescriptionsTab from './LessonDescriptionsTab';
import AdsTab from './AdsTab';
import DonationTargetsTab from './DonationTargetsTab';

// ייבוא האייקונים הקיימים בלבד מתוך הקובץ שלך
import { LogOutIcon, UsersIcon, SettingsIcon, ActivityIcon } from '../utils/icons';

// ── ניווט אנכי מקובץ בתיקיות (במקום שורת טאבים אופקית ארוכה) — כל תיקייה
// היא נושא (סקירה/משתמשים/תוכן/כספים/תקשורת/מערכת), נפתחת/נסגרת בנפרד,
// והטאב הפעיל תמיד גלוי כי התיקייה שמכילה אותו פתוחה כברירת מחדל. ──
const NAV_GROUPS = [
  {
    key: 'overview', label: 'סקירה', emoji: '📊',
    tabs: [
      { key: 'dashboard', label: 'דשבורד', Icon: ActivityIcon },
    ],
  },
  {
    key: 'people', label: 'משתמשים', emoji: '👥',
    tabs: [
      { key: 'users', label: 'משתמשים', Icon: UsersIcon },
      { key: 'activities', label: 'פעילויות', Icon: ActivityIcon },
    ],
  },
  {
    key: 'content', label: 'תוכן', emoji: '📚',
    tabs: [
      { key: 'stories', label: 'סטוריז', Icon: ActivityIcon },
      { key: 'relatedLessons', label: 'שיעורים בנושא', Icon: ActivityIcon },
      { key: 'specialDays', label: 'ימים מיוחדים', Icon: ActivityIcon },
      { key: 'quizzes', label: 'בחן את עצמך', Icon: ActivityIcon },
      { key: 'hakdashot', label: 'הקדשות', Icon: ActivityIcon },
      { key: 'parashot', label: 'פרשות השבוע', Icon: ActivityIcon },
      { key: 'lessonDescriptions', label: 'טקסט מקדים בשיעורים', Icon: ActivityIcon },
    ],
  },
  {
    key: 'finance', label: 'כספים', emoji: '💰',
    tabs: [
      { key: 'donations', label: 'צדקה', Icon: ActivityIcon },
      { key: 'donationTargets', label: 'קופות צדקה', Icon: ActivityIcon },
    ],
  },
  {
    key: 'comms', label: 'תקשורת', emoji: '🔔',
    tabs: [
      { key: 'notifications', label: 'נוטיפיקציות', Icon: ActivityIcon },
      { key: 'systemMessages', label: 'הודעות והצעת החלטה', Icon: ActivityIcon },
      { key: 'ads', label: 'פרסומות בכניסה', Icon: ActivityIcon },
      { key: 'weeklyReport', label: 'דוח שבועי לרבי', Icon: ActivityIcon },
    ],
  },
  {
    key: 'system', label: 'מערכת', emoji: '⚙️',
    tabs: [
      { key: 'settings', label: 'הגדרות', Icon: SettingsIcon },
    ],
  },
];

const findGroupKeyForTab = (tabKey) => NAV_GROUPS.find((g) => g.tabs.some((t) => t.key === tabKey))?.key;

const MainPage = ({ currentUser, onLogout }) => {
  const [activeTab, setActiveTab] = useState('users');
  const [expandedGroups, setExpandedGroups] = useState(() => new Set([findGroupKeyForTab('users')]));
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const toggleGroup = (key) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const selectTab = (tabKey) => {
    setActiveTab(tabKey);
    setMobileNavOpen(false);
  };

  const renderTab = () => {
    switch (activeTab) {
      case 'users': return <UsersTab />;
      case 'donations': return <DonationsTab />;
      case 'hakdashot': return <HakdashotTab />;
      case 'dashboard': return <DashboardTab />;
      case 'stories': return <StoriesTab />;
      case 'relatedLessons': return <RelatedLessonsTab />;
      case 'specialDays': return <SpecialDaysTab />;
      case 'quizzes': return <QuizzesTab />;
      case 'parashot': return <ParashotTab />;
      case 'settings': return <SettingsTab />;
      case 'activities': return <ActivitiesTab />;
      case 'notifications': return <NotificationsTab />;
      case 'systemMessages': return <SystemMessagesTab />;
      case 'weeklyReport': return <WeeklyReportTab />;
      case 'ads': return <AdsTab />;
      case 'donationTargets': return <DonationTargetsTab />;
      case 'lessonDescriptions': return <LessonDescriptionsTab />;
      default: return null;
    }
  };

  return (
    <div style={styles.mainContainer}>
      <style>{`
        .bo-hamburger { display: none; }
        .bo-overlay { display: none; }
        @media (max-width: 900px) {
          .bo-hamburger { display: inline-flex !important; }
          .bo-sidebar {
            position: fixed;
            top: 0;
            right: -280px;
            height: 100vh;
            z-index: 50;
            transition: right 0.25s ease;
            box-shadow: -4px 0 24px rgba(0,0,0,0.18);
          }
          .bo-sidebar.open { right: 0; }
          .bo-overlay.open {
            display: block;
            position: fixed;
            inset: 0;
            background: rgba(0,0,0,0.35);
            z-index: 40;
          }
        }
      `}</style>

      {/* Header */}
      <header style={styles.mainHeader}>
        <div style={styles.headerContent}>
          <div style={styles.headerLeft}>
            <div style={styles.headerTitleRow}>
              <button
                className="bo-hamburger"
                onClick={() => setMobileNavOpen((v) => !v)}
                style={styles.hamburgerBtn}
                aria-label="תפריט"
              >
                ☰
              </button>
              <h1 style={styles.headerTitle}>Back Office</h1>
            </div>
            <p style={styles.headerWelcome}>ברוכים הבאים, {currentUser}</p>
          </div>
          <button onClick={onLogout} style={styles.logoutBtn}>
            <LogOutIcon />
            <span>התנתק</span>
          </button>
        </div>
      </header>

      <div style={styles.body}>
        <div className={`bo-overlay ${mobileNavOpen ? 'open' : ''}`} onClick={() => setMobileNavOpen(false)} />

        {/* Sidebar — ניווט אנכי מקובץ בתיקיות */}
        <nav className={`bo-sidebar ${mobileNavOpen ? 'open' : ''}`} style={styles.sidebar}>
          {NAV_GROUPS.map((group) => {
            const expanded = expandedGroups.has(group.key);
            return (
              <div key={group.key} style={styles.navGroup}>
                <button onClick={() => toggleGroup(group.key)} style={styles.navGroupHeader}>
                  <span style={styles.navGroupEmoji}>{group.emoji}</span>
                  <span style={styles.navGroupLabel}>{group.label}</span>
                  <span style={styles.navGroupChevron}>{expanded ? '▾' : '◂'}</span>
                </button>
                {expanded && (
                  <div style={styles.navGroupTabs}>
                    {group.tabs.map((tab) => (
                      <button
                        key={tab.key}
                        onClick={() => selectTab(tab.key)}
                        style={{
                          ...styles.navTab,
                          ...(activeTab === tab.key ? styles.navTabActive : {}),
                        }}
                      >
                        <tab.Icon />
                        <span>{tab.label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* Main Content */}
        <main style={styles.mainContent}>
          <div style={styles.cardContainer}>
            <div style={styles.tabContent}>
              {renderTab()}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

const styles = {
  mainContainer: {
    background: "#f6f8fb",
    minHeight: "100vh",
    fontFamily: "system-ui, -apple-system, sans-serif",
    direction: "rtl"
  },

  mainHeader: {
    background: "#ffffff",
    borderBottom: "1px solid #e3e8ee",
    padding: "1rem 2rem",
    boxShadow: "0 1px 3px rgba(0,0,0,0.02)"
  },

  headerContent: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "12px"
  },

  headerLeft: {
    display: "flex",
    flexDirection: "column",
    gap: "2px"
  },

  headerTitleRow: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },

  hamburgerBtn: {
    alignItems: "center",
    justifyContent: "center",
    width: "36px",
    height: "36px",
    background: "#f6f8fb",
    border: "1px solid #e3e8ee",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "18px",
    color: "#1a1f36",
  },

  headerTitle: {
    fontSize: "22px",
    fontWeight: "800",
    color: "#1a1f36",
    margin: 0
  },

  headerWelcome: {
    fontSize: "13px",
    color: "#697386",
    margin: 0
  },

  logoutBtn: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    background: "#fff1f1",
    color: "#d93838",
    border: "1px solid #fbc4c4",
    padding: "8px 14px",
    borderRadius: "10px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "600",
    transition: "all 0.2s ease"
  },

  body: {
    display: "flex",
    alignItems: "flex-start",
    maxWidth: "1400px",
    margin: "0 auto",
    padding: "2rem",
    gap: "24px",
  },

  sidebar: {
    flexShrink: 0,
    width: "260px",
    background: "#ffffff",
    borderRadius: "16px",
    border: "1px solid #e3e8ee",
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.03)",
    padding: "10px",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },

  navGroup: {
    display: "flex",
    flexDirection: "column",
  },

  navGroupHeader: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    background: "transparent",
    border: "none",
    padding: "10px 10px",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "700",
    color: "#1a1f36",
    textAlign: "right",
  },

  navGroupEmoji: {
    fontSize: "16px",
  },

  navGroupLabel: {
    flex: 1,
  },

  navGroupChevron: {
    color: "#8792a2",
    fontSize: "12px",
  },

  navGroupTabs: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    paddingRight: "14px",
    marginBottom: "6px",
  },

  navTab: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    background: "transparent",
    border: "none",
    borderRight: "2px solid transparent",
    padding: "9px 10px",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "13px",
    fontWeight: "600",
    color: "#697386",
    textAlign: "right",
    transition: "all 0.15s ease",
  },

  navTabActive: {
    color: "#1f6feb",
    background: "#eef4ff",
    borderRight: "2px solid #1f6feb",
  },

  mainContent: {
    flex: 1,
    minWidth: 0,
  },

  cardContainer: {
    background: "#ffffff",
    borderRadius: "20px",
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.03)",
    border: "1px solid #e3e8ee",
    overflow: "hidden"
  },

  tabContent: {
    padding: "2rem"
  }
};

export default MainPage;
